import { useState, useRef, useEffect } from 'preact/hooks'
import { IntegrationProvider } from '@/types/integrations'

export interface FormValues {
  Name: string
  Description: string
  ApiKey: string
}

interface ProviderMeta {
  id: IntegrationProvider
  title: string
}

interface UseIntegrationFormParams {
  selectedProvider: ProviderMeta | null
  managingConnection: { Name?: string | null; Description?: string | null } | null
}

interface UseIntegrationFormReturn {
  formValues: FormValues
  setFormValues: (updater: FormValues | ((prev: FormValues) => FormValues)) => void
  fieldErrors: { Name?: string; Description?: string; ApiKey?: string }
  setFieldErrors: (errors: { Name?: string; Description?: string; ApiKey?: string }) => void
  businessErrors: { Title: string; Message: string }[]
  setBusinessErrors: (errors: { Title: string; Message: string }[]) => void
  createError: string | null
  setCreateError: (error: string | null) => void
  validate: () => { Name?: string; Description?: string; ApiKey?: string }
  handlePanelError: (raw: string) => void
  isBusinessError: (v: unknown) => v is { Title: unknown; Message: unknown }
  resetFormState: () => void
  nameInputRef: { current: HTMLInputElement | null }
}

export function useIntegrationForm({
  selectedProvider,
  managingConnection,
}: UseIntegrationFormParams): UseIntegrationFormReturn {
  const [formValues, setFormValues] = useState<FormValues>({ Name: '', Description: '', ApiKey: '' })
  const [fieldErrors, setFieldErrors] = useState<{ Name?: string; Description?: string; ApiKey?: string }>({})
  const [businessErrors, setBusinessErrors] = useState<{ Title: string; Message: string }[]>([])
  const [createError, setCreateError] = useState<string | null>(null)
  const nameInputRef = useRef<HTMLInputElement | null>(null)

  // Reset form when provider changes
  useEffect(() => {
    if (selectedProvider) {
      setFormValues({ Name: selectedProvider.title, Description: '', ApiKey: '' })
      setCreateError(null)
      setBusinessErrors([])
      setFieldErrors({})
    }
  }, [selectedProvider])

  // Reset form when managing a different connection
  useEffect(() => {
    if (managingConnection) {
      setFormValues({
        Name: managingConnection.Name || '',
        Description: managingConnection.Description || '',
        ApiKey: ''
      })
      setCreateError(null)
      setBusinessErrors([])
      setFieldErrors({})
    }
  }, [managingConnection])

  const isBusinessError = (v: unknown): v is { Title: unknown; Message: unknown } =>
    !!v && typeof v === 'object' && 'Title' in (v as Record<string, unknown>) && 'Message' in (v as Record<string, unknown>)

  const handlePanelError = (raw: string) => {
    setBusinessErrors([])
    setCreateError(null)
    if (!raw) return
    let parsed: unknown
    try { parsed = JSON.parse(raw) } catch (syntaxError) { console.warn('Failed to parse error response as JSON:', syntaxError) }
    if (Array.isArray(parsed) && parsed.every(isBusinessError)) {
      setBusinessErrors(parsed.map(p => {
        const be = p as { Title: unknown; Message: unknown }
        return { Title: String(be.Title), Message: String(be.Message) }
      }))
    } else {
      setCreateError(raw)
    }
  }

  const validate = () => {
    const errs: { Name?: string; Description?: string; ApiKey?: string } = {}
    const nameTrim = formValues.Name.trim()
    const descTrim = formValues.Description?.trim() || ''
    const apiKeyTrim = formValues.ApiKey.trim()

    if (!nameTrim) errs.Name = 'Name is required.'
    if (nameTrim.length > 100) errs.Name = 'Name exceeds maximum length (100).'
    if (descTrim.length > 500) errs.Description = 'Description exceeds maximum length (500).'
    // API key is customer-supplied for third-party providers, but server-generated
    // for Webhook — skip the required check for Webhook creates.
    const isWebhookCreate = selectedProvider?.id === IntegrationProvider.Webhook
    if (selectedProvider && !isWebhookCreate && !apiKeyTrim) errs.ApiKey = 'API Key is required.'

    setFieldErrors(errs)
    return errs
  }

  const resetFormState = () => {
    setFormValues({ Name: '', Description: '', ApiKey: '' })
    setFieldErrors({})
    setBusinessErrors([])
    setCreateError(null)
  }

  return {
    formValues,
    setFormValues,
    fieldErrors,
    setFieldErrors,
    businessErrors,
    setBusinessErrors,
    createError,
    setCreateError,
    validate,
    handlePanelError,
    isBusinessError,
    resetFormState,
    nameInputRef,
  }
}
